import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, Skeleton } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useLocation } from 'react-router-dom';

import {
  cropConnectApiClient,
  CropConnectCountry,
  CropConnectRoleDefinitions,
  DocumentFolder,
  DocumentSnapshotDto,
} from '../../../../services/cropConnectApiClient';
import {
  useContextStore,
  useDocumentsStore,
} from '../../../../stores/storeRoot';
import { handleDocumentPreview } from '../../../../helpers/filePreviewHelper';
import TooltipTranslationHelper from '../../../../helpers/TooltipTranslationHelper';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import {
  Checkbox,
  DateSearchInput,
  DocumentLink,
  FilterBox,
  FilterButton,
  Label,
  LoaderWrapper,
  SearchIconComponent,
  SearchIconInside,
  SearchInput,
  SearchInputContainer,
  SearchWrapper,
  StyledDownloadIconWrapper,
  StyledHeaderContainer,
  StyledHeaderTableCell,
  StyledTableCell,
  StyledTableHead,
  StyledTableRow,
  ViewAllWrapper,
  Wrapper,
  YearSearchInput,
} from './styles';
import Loader from '../../../components/Loader/Loader';
import { downloadBlob } from '../../../../helpers/fileDownloadHelper';
import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import PDFIcon from '../../../../assets/icons/PDFIcon';
import {
  formatDate,
  mapLocaleAccordingToNzRules,
} from '../../../../helpers/localeFormatter';
import { useI18n } from '../../../../hooks/useI18n';
import SecurityTrim from '../../../../services/auth/SecurityTrim';
import ActionButton from '../../../components/ActionButton/ActionButton';

export type AllIndividualDocumentListItemProps = {
  countryIsoCode: string | undefined;
  id?: string;
  title?: string;
  invoiceNumber?: string;
  campaignYear?: string;
  creationDate?: string;
  read?: boolean;
  showInvoiceNumber?: boolean;
  showCalendarYear?: boolean;
  category?: DocumentFolder;
};

type AllIndividualDocumentListProps = {
  title: string | ReactNode;
  pageSize?: number;
  onToggleView?: () => void;
  buttonText?: ReactNode;
  showExtraButton: boolean;
  showInvoiceNumber?: boolean;
  hideInvoiceColumn?: boolean;
  hideCalendarYearColumn?: boolean;
  prefilter?: string;
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler); // Cleanup on re-render
    };
  }, [value, delay]);

  return debouncedValue;
};

const DocumentListItem: React.FC<AllIndividualDocumentListItemProps> = ({
  countryIsoCode,
  id,
  title,
  invoiceNumber,
  campaignYear,
  creationDate,
  read,
  showInvoiceNumber,
  showCalendarYear,
  category,
}) => {
  const { t } = useI18n();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isRead, setIsRead] = useState(read);

  const { nordzuckerInternalUser } = useContextStore();

  const renderText = (text?: string | number) => {
    const displayedText = text !== undefined && text !== null ? text : '-';
    return isRead ? <span>{displayedText}</span> : <b>{displayedText}</b>;
  };

  const renderCategory = (category: DocumentFolder) => {
    switch (category) {
      case DocumentFolder.MyContract:
        return t(TranslationKeys.individual_documents_list_category_contract);
      case DocumentFolder.MyPayments:
        return t(TranslationKeys.individual_documents_list_category_payments);
      case DocumentFolder.MyNewsletter:
        return t(TranslationKeys.individual_documents_list_category_newsletter);
      case DocumentFolder.MyBusinessInfoBeet:
        return t(
          TranslationKeys.individual_documents_list_category_business_info_beet
        );
      case DocumentFolder.MyDataExportSupportData:
        return t(TranslationKeys.individual_documents_list_category_my_upload);
      default:
        return t(TranslationKeys.individual_documents_list_category_unknown);
    }
  };

  const handlePreviewClick = () => {
    setIsPreviewLoading(true);
    if (!nordzuckerInternalUser) {
      setIsRead(true);
    }
    handleDocumentPreview(id!).finally(() => {
      setIsPreviewLoading(false);
    });
  };

  const renderDownloadCell = () => {
    if (isDownloading) {
      return (
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_download_document}
          placement="top"
        >
          <LoaderWrapper>
            <Loader isFullScreen={false} size={40} />
          </LoaderWrapper>
        </TooltipTranslationHelper>
      );
    } else {
      return (
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_download_document}
          placement="top"
        >
          <IconButton
            edge="end"
            aria-label="download"
            onClick={() => {
              const alreadySet = isDownloading;
              setIsDownloading(true);
              if (!alreadySet) {
                cropConnectApiClient
                  .downloadDocument(id!)
                  .then(async (r) => {
                    if (!nordzuckerInternalUser) {
                      setIsRead(true);
                    }
                    downloadBlob(id!, r.data);
                  })
                  .catch((r) => {
                    console.error(
                      `Error downloading document: ${JSON.stringify(r)}`
                    );
                  })
                  .finally(() => {
                    setIsDownloading(false);
                  });
              }
            }}
          >
            <StyledDownloadIconWrapper>
              <DownloadIcon />
            </StyledDownloadIconWrapper>
          </IconButton>
        </TooltipTranslationHelper>
      );
    }
  };

  return (
    <StyledTableRow key={id}>
      <StyledTableCell align="left">
        <TooltipTranslationHelper
          titleKey={TranslationKeys.documents_list_preview_document}
          placement="top"
        >
          {isPreviewLoading ? (
            <LoaderWrapper>
              <Loader isFullScreen={false} size={40} />
            </LoaderWrapper>
          ) : (
            <DocumentLink onClick={handlePreviewClick}>
              <IconButton>
                <PDFIcon />
              </IconButton>
              {renderText(title)}
            </DocumentLink>
          )}
        </TooltipTranslationHelper>
      </StyledTableCell>
      {showInvoiceNumber && (
        <StyledTableCell align="center">
          {renderText(invoiceNumber)}
        </StyledTableCell>
      )}
      {showCalendarYear && (
        <StyledTableCell align="center">
          {renderText(campaignYear)}
        </StyledTableCell>
      )}
      <StyledTableCell align="center">
        {renderText(
          formatDate(creationDate!, mapLocaleAccordingToNzRules(countryIsoCode))
        )}
      </StyledTableCell>
      <StyledTableCell align="center">
        {renderText(renderCategory(category))}
      </StyledTableCell>
      <StyledTableCell>{renderDownloadCell()}</StyledTableCell>
    </StyledTableRow>
  );
};

// Phil, 30.8.24: If we need to show dynamic header texts, we should receive them by the backend and display them accordingly here :).
const DocumentListHeader: React.FC<{
  showInvoiceNumber: boolean;
  onFullReportClick?: () => void;
  buttonText?: string | ReactNode;
  showExtraButton?: boolean;
  showCalendarYear?: boolean;
  onNameSearchInput: (searchString: string) => void;
  onYearSearchInput: (searchString: string) => void;
  onDateSearchInput: (searchString: string) => void;
  onCategoryFilterChange: (selectedOptions: string[]) => void;
  country: string | undefined;
}> = ({
  showInvoiceNumber,
  onFullReportClick,
  buttonText,
  showExtraButton,
  showCalendarYear,
  onNameSearchInput,
  onYearSearchInput,
  onDateSearchInput,
  onCategoryFilterChange,
  country,
}) => {
  const { t } = useI18n();
  const options = [
    DocumentFolder.MyContract,
    DocumentFolder.MyPayments,
    DocumentFolder.MyDataExportSupportData,
  ];
  if (country === 'FI') {
    options.push(DocumentFolder.MyNewsletter);
  }
  if (country === 'DE') {
    options.push(DocumentFolder.MyNewsletter);
    options.push(DocumentFolder.MyBusinessInfoBeet);
  }
  const [selectedOptions, setSelectedOptions] = useState(options);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterBoxStyle, setFilterBoxStyle] = useState({ top: 0, left: 0 });
  const filterButtonRef = useRef(null);
  const filterRef = useRef(null);

  const [nameSearchText, setNameSearchText] = useState('');
  const [yearSearchText, setYearSearchText] = useState('');
  const [dateSearchText, setDateSearchText] = useState('');
  const [isNameSearchInputVisible, setIsNameSearchInputVisible] =
    useState(false);
  const [isYearSearchInputVisible, setIsYearSearchInputVisible] =
    useState(false);
  const [isDateSearchInputVisible, setIsDateSearchInputVisible] =
    useState(false);
  const nameSearchInputRef = useRef(null);
  const yearSearchInputRef = useRef(null);
  const dateSearchInputRef = useRef(null);

  const debouncedNameSearchText = useDebounce(nameSearchText, 500);
  const debouncedDateSearchText = useDebounce(dateSearchText, 500);

  useEffect(() => {
    onNameSearchInput(debouncedNameSearchText);
  }, [debouncedNameSearchText, onNameSearchInput]);

  useEffect(() => {
    onDateSearchInput(debouncedDateSearchText);
  }, [debouncedDateSearchText, onDateSearchInput]);

  const handleNameSearchClick = () => {
    setIsNameSearchInputVisible(true);
    setTimeout(() => nameSearchInputRef.current?.focus(), 0); // Ensure input gets focus
  };

  const handleYearSearchClick = () => {
    setIsYearSearchInputVisible(true);
    setTimeout(() => yearSearchInputRef.current?.focus(), 0); // Ensure input gets focus
  };

  const handleDateSearchClick = () => {
    setIsDateSearchInputVisible(true);
    setTimeout(() => dateSearchInputRef.current?.focus(), 0); // Ensure input gets focus
  };
  const handleNameSearchBlur = () => {
    if (!nameSearchText) {
      setIsNameSearchInputVisible(false);
    }
  };
  const handleYearSearchBlur = () => {
    if (!yearSearchText) {
      setIsYearSearchInputVisible(false);
    }
  };
  const handleDateSearchBlur = () => {
    if (!dateSearchText) {
      setIsDateSearchInputVisible(false);
    }
  };

  const handleChange = (event) => {
    const { value, checked } = event.target;
    setSelectedOptions((prev) => {
      const options = checked
        ? [...prev, value]
        : prev.filter((item) => item !== value);
      onCategoryFilterChange(options);
      return options;
    });
  };

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      setFilterBoxStyle({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
  }, [isFilterOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ensure the click is not on the button or filter box
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setTimeout(() => setIsFilterOpen(false), 0);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const renderCategory = (category: DocumentFolder) => {
    switch (category) {
      case DocumentFolder.MyContract:
        return t(TranslationKeys.individual_documents_list_category_contract);
      case DocumentFolder.MyPayments:
        return t(TranslationKeys.individual_documents_list_category_payments);
      case DocumentFolder.MyNewsletter:
        return t(TranslationKeys.individual_documents_list_category_newsletter);
      case DocumentFolder.MyBusinessInfoBeet:
        return t(
          TranslationKeys.individual_documents_list_category_business_info_beet
        );
      case DocumentFolder.MyDataExportSupportData:
        return t(TranslationKeys.individual_documents_list_category_my_upload);
      default:
        return t(TranslationKeys.individual_documents_list_category_unknown);
    }
  };

  return (
    <>
      <StyledHeaderContainer>
        <h2>{t(TranslationKeys.all_individual_documents_list_title)}</h2>
        {showExtraButton && (
          <SecurityTrim
            requiredBusinessPartnerRoles={[
              CropConnectRoleDefinitions.Grower_DE,
              CropConnectRoleDefinitions.Grower_Organic_DE,
            ]}
          >
            <ActionButton
              variant="accept-blue"
              buttonText={buttonText}
              onClickAction={onFullReportClick ?? (() => {})}
            />
          </SecurityTrim>
        )}
      </StyledHeaderContainer>
      <StyledTableHead>
        <StyledTableRow key="header">
          <StyledHeaderTableCell align="left">
            {t(TranslationKeys.documents_list_column_header_filename)}
            <SearchWrapper>
              {!isNameSearchInputVisible && (
                <SearchIconComponent onClick={handleNameSearchClick} />
              )}
              <SearchInputContainer>
                <SearchInput
                  ref={nameSearchInputRef}
                  type="text"
                  value={nameSearchText}
                  onChange={(e) => {
                    setNameSearchText(e.target.value);
                  }}
                  onBlur={handleNameSearchBlur}
                  placeholder="Search..."
                  visible={isNameSearchInputVisible}
                />
                {isNameSearchInputVisible && <SearchIconInside />}
              </SearchInputContainer>
            </SearchWrapper>
          </StyledHeaderTableCell>
          {showInvoiceNumber && (
            <StyledHeaderTableCell align="center">
              {t(TranslationKeys.document_list_column_header_invoiceId)}
            </StyledHeaderTableCell>
          )}
          {showCalendarYear && (
            <StyledHeaderTableCell align="center">
              {t(TranslationKeys.documents_list_column_header_businessYear)}
              <SearchWrapper>
                {!isYearSearchInputVisible && (
                  <SearchIconComponent onClick={handleYearSearchClick} />
                )}
                <SearchInputContainer>
                  <YearSearchInput
                    ref={yearSearchInputRef}
                    type="text"
                    value={yearSearchText}
                    onChange={(e) => {
                      setYearSearchText(e.target.value);
                      onYearSearchInput(e.target.value);
                    }}
                    onBlur={handleYearSearchBlur}
                    placeholder="Search..."
                    visible={isYearSearchInputVisible}
                  />
                  {isYearSearchInputVisible && <SearchIconInside />}
                </SearchInputContainer>
              </SearchWrapper>
            </StyledHeaderTableCell>
          )}
          <StyledHeaderTableCell align="center">
            {t(TranslationKeys.documents_list_column_header_creationDate)}
            <SearchWrapper>
              {!isDateSearchInputVisible && (
                <SearchIconComponent onClick={handleDateSearchClick} />
              )}
              <SearchInputContainer>
                <DateSearchInput
                  ref={dateSearchInputRef}
                  type="text"
                  value={dateSearchText}
                  onChange={(e) => {
                    setDateSearchText(e.target.value);
                  }}
                  onBlur={handleDateSearchBlur}
                  placeholder="Search..."
                  visible={isDateSearchInputVisible}
                />
                {isDateSearchInputVisible && <SearchIconInside />}
              </SearchInputContainer>
            </SearchWrapper>
          </StyledHeaderTableCell>
          <StyledHeaderTableCell align="center">
            {t(TranslationKeys.documents_list_column_header_category)}
            <FilterButton
              ref={filterButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                setIsFilterOpen(!isFilterOpen);
              }}
            >
              <FilterAltOutlinedIcon />
            </FilterButton>
            {isFilterOpen && (
              <FilterBox
                ref={filterRef}
                style={{
                  top: filterBoxStyle.top,
                  left: filterBoxStyle.left,
                  position: 'absolute',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {options.map((option) => (
                  <Label key={option}>
                    <Checkbox
                      type="checkbox"
                      value={option}
                      checked={selectedOptions.includes(option)}
                      onChange={handleChange}
                    />
                    {renderCategory(option)}
                  </Label>
                ))}
              </FilterBox>
            )}
          </StyledHeaderTableCell>
          <StyledHeaderTableCell>
            {t(TranslationKeys.documents_list_column_header_actions)}
          </StyledHeaderTableCell>
        </StyledTableRow>
      </StyledTableHead>
    </>
  );
};

const AllIndividualDocumentsList: React.FC<AllIndividualDocumentListProps> = ({
  onToggleView,
  buttonText,
  showExtraButton,
  hideInvoiceColumn,
  hideCalendarYearColumn,
}) => {
  const location = useLocation();
  const [documents, setDocuments] = useState<DocumentSnapshotDto[]>([]);
  const [nameSearchString, setNameSearchString] = useState<string>('');
  const [yearSearchString, setYearSearchString] = useState<string>('');
  const [dateSearchString, setDateSearchString] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    DocumentFolder.MyPayments,
    DocumentFolder.MyNewsletter,
    DocumentFolder.MyContract,
    DocumentFolder.MyBusinessInfoBeet,
    DocumentFolder.MyDataExportSupportData,
  ]);
  const [showInvoiceNumberForCountry, setShowInvoiceNumberForCountry] =
    useState<boolean>(false);
  const [showCalendarYear, setShowCalendarYear] = useState<boolean>(false);
  const {
    isUpdating,
    isContextUpdating,
    isInitializing,
    getAllInboxDocumentsPage,
  } = useDocumentsStore();
  const { activeBpNumber, activeBpNumberCountry } = useContextStore();
  const [isDisplayAll, setIsDisplayAll] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    loadDocumentsPageIntern();
  }, [activeBpNumber]);

  useEffect(() => {
    useDocumentsStore.getState().initializeDocumentsStore();
  }, [location]);

  useMemo(() => {
    setShowInvoiceNumberForCountry(
      !(
        hideInvoiceColumn ||
        activeBpNumberCountry?.toUpperCase() === CropConnectCountry.DE
      )
    );
    setShowCalendarYear(!hideCalendarYearColumn);
  }, [activeBpNumberCountry, hideInvoiceColumn, showCalendarYear]);

  const loadDocumentsPageIntern = async () => {
    try {
      const retrievalResult = await getAllInboxDocumentsPage(
        activeBpNumberCountry,
        10000
      );
      if (!retrievalResult?.documents) {
        console.error('No documents returned from API');
      }
      setDocuments(retrievalResult?.documents ?? []);
    } catch (e) {
      console.error('Error loading documents:', e);
      setDocuments([]);
    }
  };

  const handleViewAll = () => {
    setIsDisplayAll(true);
  };

  const renderItemRow = (item: DocumentSnapshotDto) => (
    <DocumentListItem
      key={item.id}
      id={item.id}
      invoiceNumber={
        item.additionalData ? item.additionalData!['InvoiceNumber'] : undefined
      }
      countryIsoCode={activeBpNumberCountry}
      title={item.name}
      campaignYear={item.campaignYear}
      creationDate={item.creationDate}
      read={item.read}
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
      category={item.documentFolder}
    />
  );

  const renderHeaderRow = () => (
    <DocumentListHeader
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
      onFullReportClick={onToggleView}
      buttonText={buttonText}
      showExtraButton={showExtraButton}
      onNameSearchInput={(nameSearchString) => {
        setNameSearchString(nameSearchString);
      }}
      onYearSearchInput={(yearSearchString) => {
        setYearSearchString(yearSearchString);
      }}
      onDateSearchInput={(dateSearchString) => {
        setDateSearchString(dateSearchString);
      }}
      onCategoryFilterChange={(selectedOptions) => {
        setSelectedOptions(selectedOptions);
      }}
      country={activeBpNumberCountry}
    />
  );

  const filteredDocuments = documents
    .filter((document) =>
      document.name
        ?.toLowerCase()
        .includes(nameSearchString.toLowerCase() ?? '')
    )
    .filter(
      (document) =>
        (!document.campaignYear && !yearSearchString) ||
        (document.campaignYear &&
          document.campaignYear
            ?.toLowerCase()
            .includes(yearSearchString.toLowerCase() ?? ''))
    )
    .filter(
      (document) =>
        document.creationDate &&
        formatDate(
          document.creationDate!,
          mapLocaleAccordingToNzRules(activeBpNumberCountry)
        ).includes(dateSearchString ?? '')
    )
    .filter(
      (document) =>
        document.documentFolder &&
        selectedOptions.includes(document.documentFolder)
    );

  return (
    <Wrapper>
      {isUpdating || isContextUpdating || isInitializing ? (
        <div className="skeleton-table">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="skeleton-row">
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="90%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <>
          <StyledHeaderContainer>
            <h3>{t(TranslationKeys.my_payments_documents_list_title)}</h3>
            {showExtraButton && (
              <SecurityTrim
                requiredBusinessPartnerRoles={[
                  CropConnectRoleDefinitions.Grower_DE,
                  CropConnectRoleDefinitions.ServiceProvider_DE,
                  CropConnectRoleDefinitions.Grower_Organic_DE,
                ]}
              >
                <ActionButton
                  variant="accept-blue"
                  buttonText={buttonText}
                  onClickAction={onToggleView}
                />
              </SecurityTrim>
            )}
          </StyledHeaderContainer>
          <p>{t(TranslationKeys.documents_list_no_documents_found)}</p>
        </>
      ) : (
        <>
          {renderHeaderRow()}
          {filteredDocuments
            .slice(0, isDisplayAll ? filteredDocuments.length : 10)
            .map(renderItemRow)}
          {!isDisplayAll && filteredDocuments.length > 10 && (
            <ViewAllWrapper>
              <ActionButton
                variant="accept-blue-light"
                buttonText={t(TranslationKeys.documents_list_button_view_all)}
                onClickAction={handleViewAll}
              />
            </ViewAllWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default AllIndividualDocumentsList;
